/**
 * Whose house the measurement report is for.
 *
 * A rep uploads a PDF onto a job, and the one mistake that survives every other
 * check is uploading the *wrong* PDF: the measurements parse, the form fills,
 * the estimate prices — against somebody else's roof. Nothing downstream can
 * catch it, because a roof is only ever a set of numbers by then.
 *
 * So the name is read off the report and held up against the contact on the
 * job. Jonathon, 2026-08-25: "if we upload a roof measurement report and the
 * name in the roof measurement report pdf doesn't come close to matching the
 * customer's name I want it to just alert us."
 *
 * **It alerts; it never blocks.** Reports are addressed to whoever ordered
 * them, and that is legitimately not always the homeowner — a spouse, a
 * property manager, an adjuster, the company itself. A rep who knows that
 * carries on. What must not happen is nobody looking.
 *
 * Deliberately generous about what counts as a match, because a false alarm on
 * every upload is an alarm nobody reads:
 *
 *   - "Craig Hurst" matches "Hurst, Craig", "CRAIG HURST" and "Craig A Hurst"
 *   - "C. Hurst" matches "Craig Hurst" — an initial is a first name
 *   - "Craig & Susan Hurst" matches "Susan Hurst" — couples share a report
 *   - "Craig Hurst" does not match "Marcus Delgado", which is the whole point
 */

/** Words that are not part of anybody's name. */
const TITLES = new Set([
  "mr",
  "mrs",
  "ms",
  "miss",
  "dr",
  "rev",
  "sir",
  "mr.",
  "mrs.",
  "ms.",
  "dr.",
]);

const SUFFIXES = new Set(["jr", "sr", "ii", "iii", "iv", "v", "md", "phd"]);

/** The labels a measuring service prints the customer's name after. */
const NAME_LABELS = [
  "prepared for",
  "report prepared for",
  "prepared exclusively for",
  "property owner",
  "homeowner",
  "home owner",
  "customer name",
  "customer",
  "client name",
  "client",
  "owner",
  "report for",
  "attention",
  "attn",
];

/**
 * The words of a name, lowercased, with titles and suffixes dropped.
 *
 * "Hurst, Craig" comes back as ["craig", "hurst"] — a comma is the report
 * saying surname first, and reordering it here means every comparison below
 * gets to assume the ordinary order.
 */
export function nameTokens(raw: string): string[] {
  let text = raw
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  // "Hurst, Craig" → "Craig Hurst". Only the first comma: anything after a
  // second one is an address or a suffix, and neither is part of the name.
  const comma = text.indexOf(",");
  if (comma !== -1) {
    const after = text
      .slice(comma + 1)
      .split(",")[0]!
      .trim();
    const before = text.slice(0, comma).trim();
    // Not a reorder when what follows the comma is a suffix — "Hurst, Jr".
    if (after && !SUFFIXES.has(after.replace(/\./g, ""))) {
      text = `${after} ${before}`;
    } else {
      text = before;
    }
  }

  return text
    .split(/[^a-z0-9'&-]+/)
    .map((word) => word.replace(/^['-]+|['-]+$/g, ""))
    .filter(Boolean)
    .filter((word) => word !== "&" && word !== "and")
    .filter((word) => !TITLES.has(word) && !SUFFIXES.has(word));
}

/** Two first names that could be the same person: equal, or an initial. */
function firstNamesAgree(a: string, b: string): boolean {
  if (a === b) return true;
  if (a.length === 1 || b.length === 1) return a[0] === b[0];
  // "Mike" for "Michael", "Sue" for "Susan" — a shortening, not a different
  // person. Four characters, because "Jo" would otherwise match "John",
  // "Joseph", "Jordan" and "Joanna" alike.
  const [shorter, longer] = a.length < b.length ? [a, b] : [b, a];
  return shorter.length >= 4 && longer.startsWith(shorter);
}

/**
 * Do these two read as the same household?
 *
 * The surname has to match. Given that, any given name in common is enough —
 * which is what lets a report addressed to both spouses match a job in one of
 * their names.
 *
 * A one-word name on either side falls back to "that word appears in the other"
 * — a report addressed to "Hurst" against a job for "Craig Hurst" is not a
 * mismatch worth stopping a rep for.
 */
export function sameHumanName(a: string, b: string): boolean {
  const left = nameTokens(a);
  const right = nameTokens(b);
  if (left.length === 0 || right.length === 0) return true;

  if (left.length === 1 || right.length === 1) {
    const [one, other] = left.length === 1 ? [left, right] : [right, left];
    return other.some((word) => firstNamesAgree(word, one[0]!));
  }

  const surname = (parts: string[]) => parts[parts.length - 1]!;
  if (surname(left) !== surname(right)) return false;

  const givens = (parts: string[]) => parts.slice(0, -1);
  return givens(left).some((one) =>
    givens(right).some((other) => firstNamesAgree(one, other)),
  );
}

/** A line that is plainly not a person: an address, an email, a phone number. */
function looksLikeAName(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length < 2 || trimmed.length > 60) return false;
  if (/\d/.test(trimmed)) return false;
  if (trimmed.includes("@")) return false;
  return /[a-z]/i.test(trimmed);
}

/**
 * The customer's name as the report gives it, or null.
 *
 * Scans for a label and takes what follows it — on the same line where the
 * report puts it there, and otherwise on the next line, which is where a
 * two-column layout leaves it once the text has been flattened.
 */
export function reportedCustomerName(text: string): string | null {
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  for (const [at, line] of lines.entries()) {
    const lower = line.toLowerCase();
    for (const label of NAME_LABELS) {
      if (!lower.startsWith(label)) continue;
      // What follows the label on this line, once the punctuation between them
      // is gone.
      const rest = line.slice(label.length).replace(/^[\s:\-–—]+/, "");
      if (looksLikeAName(rest)) return rest.trim();
      // Nothing on this line: the value is on the next one.
      const next = lines[at + 1];
      if (rest === "" && next && looksLikeAName(next)) return next.trim();
    }
  }
  return null;
}

/**
 * What to warn about, if anything.
 *
 * Null means carry on — either the names agree, or the report never named
 * anybody, or the job has no contact yet. Only a name that is present on both
 * sides and disagrees is worth a rep's attention.
 */
export function reportNameMismatch(
  reportText: string,
  customerName: string | null | undefined,
): { reported: string; customer: string } | null {
  const reported = reportedCustomerName(reportText);
  const customer = (customerName ?? "").trim();
  if (!reported || !customer) return null;
  if (sameHumanName(reported, customer)) return null;
  return { reported, customer };
}
